/**
 * Полоса вкладок кабинета оператора (вынесена из OperatorScreen 28.09).
 *
 * 28.09 (ТЗ «Юниверситет»): «Полоса вкладок … уже сейчас не влезает в 375 px:
 * 9 вкладок, у .tabs стоит overflow: hidden … сделай горизонтальную прокрутку
 * без видимой полосы, маску по краям оставь, выбранную вкладку прокручивай в
 * видимую зону». Прокрутка — только самой полосы и вручную (scrollTo): у
 * scrollIntoView в WebView Telegram уезжают и предки (.main, страница).
 */
import { useLayoutEffect, useRef, type ReactElement, type SVGProps } from "react";
import {
  TabGames, TabUni, TabSchedule, TabReports, TabSalary, TabTelegram, TabExtra, TabBounty, TabAnketa,
} from "./icons";
import css from "../OperatorScreen.module.css";

export type TabId = "games" | "uni" | "sched" | "reports" | "salary" | "tg" | "extra" | "bounty" | "anketa";

type IconC = (p: SVGProps<SVGSVGElement>) => ReactElement;

export const TABS: { id: TabId; label: string; Icon: IconC }[] = [
  { id: "games", label: "Дашборд", Icon: TabGames },
  { id: "uni", label: "Юниверситет", Icon: TabUni },
  { id: "sched", label: "График", Icon: TabSchedule },
  { id: "reports", label: "Отчёты", Icon: TabReports },
  { id: "anketa", label: "Анкета", Icon: TabAnketa },
  { id: "salary", label: "Зарплата", Icon: TabSalary },
  { id: "tg", label: "Telegram", Icon: TabTelegram },
  { id: "extra", label: "Доп", Icon: TabExtra },
  { id: "bounty", label: "Bounty", Icon: TabBounty },
];

export function OpTabs({ tab, onTab }: { tab: TabId; onTab: (id: TabId) => void }) {
  const stripRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef(true);

  /* Выбранная вкладка — по центру видимой зоны полосы (у краёв — упор в 0/максимум).
     Подпись есть только у активной, поэтому меряем после отрисовки (layout effect).
     offsetLeft — от .tabs: у неё position: relative. Первый кадр — без анимации. */
  useLayoutEffect(() => {
    const s = stripRef.current;
    const el = s?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!s || !el) return;
    const left = Math.max(0, el.offsetLeft - (s.clientWidth - el.offsetWidth) / 2);
    const behavior: ScrollBehavior = firstRef.current ? "auto" : "smooth";
    firstRef.current = false;
    if (typeof s.scrollTo === "function") s.scrollTo({ left, behavior });
    else s.scrollLeft = left;
  }, [tab]);

  return (
    <div className={css.tabsWrap}>
      <div className={css.tabs} ref={stripRef} role="tablist">
        {TABS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={`${css.tab} ${tab === id ? css.active : ""}`}
            aria-label={label}
            onClick={() => onTab(id)}
          >
            <Icon aria-hidden="true" />
            {tab === id ? <span>{label}</span> : null}
          </button>
        ))}
      </div>
    </div>
  );
}
